import { Suspense, lazy, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useSceneStore from '../stores/sceneStore';
import { applyMainCameraPathPose } from '../utils/mainCamera';
import { loadChart3DSnapshots } from '../utils/chart3dSnapshot';
import useGisStore from '../stores/gisStore';
import { fetchScene } from '../api/scenes';
import { fetchModels } from '../api/models';
import AppLoading from '../components/AppLoading';
import { initDataBindingsFromConfig, useViewerDataLink } from '../hooks/useViewerDataLink';

const SceneCanvas = lazy(() => import('../components/scene/SceneCanvas'));

// 2026-08-31｜场景发布/预览页（只读 3D 展示）：/preview/scene/:id
// 发布按钮给出本页链接，供客户/外部访问（只读场景，可漫游/自动游览）
// 2026-09-27 修复（用户反馈：发布链接打开黑屏）：①此前从不拉取模型（编辑器由 SceneEditor 拉取）→
//   改为 fetchScene + fetchModels 并行加载，并补齐 markers/flyLines/lights/clipping/GIS 等展示配置；
//   ②黑屏另一半原因见 SceneCanvas MainSceneRenderPass（displayOnly 无 composer 时主场景无人渲染）。
export default function ScenePreview() {
  const { id } = useParams<{ id: string }>();
  // DW-P0-5：预览态数据链路（设备 WS + 绑定回填 + 视觉规则 + 数据绑定），与 Viewer 同源
  useViewerDataLink(id);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([fetchScene(id), fetchModels(id)])
      .then(([scene, models]) => {
        if (cancelled) return;
        const st = useSceneStore.getState();
        const cfg = (scene as any).config || {};
        st.setScene(scene as any);
        st.setModels(models as any);
        if (cfg.environment) st.loadEnvironment(cfg.environment);
        if (cfg.effects) st.setEffects(cfg.effects);
        if (cfg.markers) st.setMarkers(cfg.markers);
        if (cfg.flyLines) st.setFlyLines(cfg.flyLines);
        if (cfg.waypoints) st.setWaypoints(cfg.waypoints);
        if (cfg.browseRoute) st.setBrowseRoute(cfg.browseRoute);
        if (cfg.clipping) st.setClipping(cfg.clipping);
        if (cfg.carousel) st.setCarousel(cfg.carousel);
        if (cfg.lights) st.lights = cfg.lights;
        // 2026-10-17（体积元素 S1）
        if (cfg.volumes) st.volumes = cfg.volumes;
        if (cfg.annotations) st.annotations = cfg.annotations;
        st.setSpikeChart3D?.((cfg.spikeChart3D as never) ?? null);
        st.setSpikeBoard3D?.((cfg.spikeBoard3D as never) ?? null);
        st.setSpikeHud3D?.((cfg.spikeHud3D as never) ?? null);
        loadChart3DSnapshots(cfg.chart3dSnapshots);
        st.setChart3DObjects?.((cfg.chart3dObjects as never) ?? []);
        if (cfg.cameraStrategy) st.setCameraStrategy(cfg.cameraStrategy);
        if (cfg.savedCamera) st.setSavedCamera(cfg.savedCamera);
        // 2026-10-15（P0-④）：鸟瞰相机 + 主相机（预览与编辑器/发布同源）
        if (cfg.birdsEyeCamera) st.setBirdsEyeCamera(cfg.birdsEyeCamera);
        if (cfg.mainCamera) st.setMainCamera(cfg.mainCamera);
        // 2026-10-16（P1-3）：主相机=漫游相机 → 应用其位姿（同源）
        applyMainCameraPathPose(cfg as never, st as never);
        if (cfg.gis) useGisStore.getState().restore(cfg.gis);
        // 2026-10-06：展示区域（发布/预览与编辑器一致裁剪）
        useSceneStore.getState().setDisplayRegion(cfg.displayRegion ?? null);
        // 2026-10-07：模型轴心修正（预览/发布一致）
        useSceneStore.getState().setPivotOffsets(cfg.pivotOffsets ?? {});
        // DW-P0-5：数据源与属性绑定随场景恢复（预览态原缺失，构件动态数据静默）
        initDataBindingsFromConfig(cfg);
        st.setDirty(false);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#000', position: 'relative', overflow: 'hidden' }}>
      <Suspense fallback={<AppLoading text="场景加载中（大模型约 1~2 分钟）" />}>
        {id && <SceneCanvas sceneId={id} displayOnly alwaysRender />}
      </Suspense>
    </div>
  );
}
